// src/App.jsx
// Description: Main Mudo app entry with two-shell architecture (Studio + Visitor)
// Routes users to appropriate shell based on role selection
// Flow: /start (public) -> /auth -> /dashboard or /discover based on mudo:userRole

import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, Outlet } from "react-router-dom";
import ThemeProvider from "./core/theme/ThemeProvider";
import CeramicsWorldLayout from "./worlds/ceramics/CeramicsWorldLayout";
import PlannerShell from "./worlds/ceramics/components/planner/PlannerShell";
import ArchitectureWorldLayout from "./worlds/architecture/ArchitectureWorldLayout";
import AdventureWorldLayout from "./worlds/adventure/AdventureWorldLayout";
import ArchitectureComingSoon from "./worlds/architecture/pages/ComingSoon.jsx";
import AdventureComingSoon from "./worlds/adventure/pages/ComingSoon.jsx";
import Planner from "./components/Planner/Planner";
import { useAuth } from "./contexts/AuthContext";

// Shells
import StudioShell from "./layouts/StudioShell";
import VisitorShell from "./layouts/VisitorShell";

// Route guards
import RequireSubscription from "./components/RouteGuards/RequireSubscription";

// Pages
import ResetPassword from "./components/Auth/ResetPassword";
import AuthCallback from "./components/Auth/AuthCallback";
import SelectRolePage from "./pages/SelectRolePage";
import StartGate from "./pages/StartGate";
import DiscoverMarketplacePage from "./pages/visitor/DiscoverMarketplacePage";
import MyOffersPage from "./pages/visitor/MyOffersPage";
import LiquidGlassAuthPage from "./pages/auth/LiquidGlassAuthPage";

// Vault (provider stays here)
import { VaultProvider } from "./contexts/VaultContext";
import LegalTermsPage from "./pages/LegalTermsPage";
import StripeOnboardingComplete from "./pages/payments/StripeOnboardingComplete";
import WorkshopBookingSuccess from "./pages/workshops/WorkshopBookingSuccess";
import SpaceBookingSuccess from "./pages/spaces/SpaceBookingSuccess";
import StripeReturn from "./pages/stripe/StripeReturn";
import StripeReturnPage from "./pages/stripe/StripeReturnPage";
import PaymentSuccess from "./pages/PaymentSuccess.jsx";
import AccessDebugPanel from "./components/Debug/AccessDebugPanel";

const isDev = import.meta?.env?.MODE === "development";
const debugGuardLog = (...args) => {
  if (isDev) console.log("[AUTH GUARD]", ...args);
};

/* =============================
   Auth helpers (single-user)
   ============================= */
function RequireAuth({ children }) {
  const { user, authLoading, isAuthReady } = useAuth();
  const location = useLocation();
  
  // Allow rendering immediately - auth-dependent features will gate themselves
  if (!isAuthReady && !user) {
    debugGuardLog("RequireAuth: auth pending, allowing render", { authLoading, isAuthReady });
    return children || <Outlet />;
  }
  
  if (!user) {
    debugGuardLog("RequireAuth: redirect to /auth", { path: location?.pathname });
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }
  
  debugGuardLog("RequireAuth: allow", { path: location?.pathname, userId: user?.id || null });
  return children || <Outlet />;
}

/* =============================
   Onboarding Guard
   ============================= */
function OnboardingGuard() {
  // Bypass onboarding; always allow navigation
  // No longer blocks on authReady - let routes render immediately
  return <Outlet />;
}

/* =============================
   RoleRedirect - Smart redirect based on saved role
   ============================= */
function RoleRedirect() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let role = localStorage.getItem("mudo:userRole");
    
    // If role is null/undefined and user is authenticated, set default to "studio"
    // This prevents routing loops for paid users who haven't explicitly set a role
    if (!role) {
      role = "studio";
      try {
        localStorage.setItem("mudo:userRole", role);
      } catch {}
    }
    
    if (role === "studio") {
      navigate("/dashboard/home", { replace: true });
    } else if (role === "visitor") {
      navigate("/discover", { replace: true });
    } else {
      navigate("/start", { replace: true });
    }
  }, [navigate, user]);

  return null;
}

/* =============================
   Root Guard - Redirect all users to /start on root access
   ============================= */
function RootGuard() {
  return <Navigate to="/start" replace />;
}

/* =============================
   Root router with two shells
   ============================= */
function RootRoutes() {
  const navigate = useNavigate();
  const { user, authLoading, isAuthReady } = useAuth();

  const handleLoginSuccess = () => {
    let role = localStorage.getItem("mudo:userRole");

    // Default to "studio" if no role set after login
    if (!role) {
      role = "studio";
      try {
        localStorage.setItem("mudo:userRole", role);
      } catch {}
    }

    if (role === "studio") {
      navigate("/dashboard/home", { replace: true });
    } else if (role === "visitor") {
      navigate("/discover", { replace: true });
    } else {
      navigate("/start", { replace: true });
    }
  };

  // Allow auth page to render immediately - no blocking spinner
  const authElement = user ? <RoleRedirect /> : <LiquidGlassAuthPage onLogin={handleLoginSuccess} />;

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/start" element={<StartGate />} />
      <Route path="/" element={<RootGuard />} />

      {/* Auth routes (public) */}
      <Route path="/auth" element={authElement} />
      <Route path="/login" element={<Navigate to="/auth" replace />} />

      {/* Auth Callback: Handle magic link authentication */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/reset" element={<ResetPassword />} />

      {/* Authenticated-only routes */}
      <Route element={<RequireAuth />}>
        <Route element={<OnboardingGuard />}>
          <Route path="/account/role" element={<SelectRolePage />} />
          {/* Studio world (gated by subscription) */}
          <Route
            path="/dashboard/*"
            element={
              <RequireSubscription>
                <StudioShell />
              </RequireSubscription>
            }
          />
          {/* Visitor world (always free) */}
          <Route path="/discover/*" element={<VisitorShell />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payments/stripe-onboarding-complete" element={<StripeOnboardingComplete />} />
          <Route path="/stripe/return" element={<StripeReturnPage />} />
          <Route path="/stripe/refresh" element={<StripeReturn mode="refresh" />} />
          <Route path="/workshops/booking-success" element={<WorkshopBookingSuccess />} />
          <Route path="/spaces/booking-success" element={<SpaceBookingSuccess />} />
          <Route path="/visitor/offers" element={<MyOffersPage />} />
          <Route path="/discover/marketplace" element={<DiscoverMarketplacePage />} />
          <Route path="/dashboard/legal/terms" element={<LegalTermsPage />} />
        </Route>
      </Route>

      {/* Ceramics namespaced routes mirror existing functionality */}
      <Route path="/ceramics/*" element={<CeramicsWorldLayout />}>
        <Route path="auth" element={authElement} />
        <Route path="login" element={<Navigate to="/auth" replace />} />
        <Route path="auth/callback" element={<AuthCallback />} />
        <Route path="auth/reset" element={<ResetPassword />} />

        <Route path="start" element={<Navigate to="/start" replace />} />

        <Route element={<RequireAuth />}>
          <Route element={<OnboardingGuard />}>
            {/* Studio world (gated by subscription) */}
            <Route
              path="dashboard/*"
              element={
                <RequireSubscription>
                  <StudioShell />
                </RequireSubscription>
              }
            />
            {/* Visitor world (always free) */}
            <Route path="discover/*" element={<VisitorShell />} />
            <Route path="planner" element={<PlannerShell><Planner /></PlannerShell>} />
            <Route path="payments/stripe-onboarding-complete" element={<StripeOnboardingComplete />} />
            <Route path="stripe/return" element={<StripeReturnPage />} />
            <Route path="stripe/refresh" element={<StripeReturn mode="refresh" />} />
            <Route path="workshops/booking-success" element={<WorkshopBookingSuccess />} />
            <Route path="spaces/booking-success" element={<SpaceBookingSuccess />} />
            <Route path="visitor/offers" element={<MyOffersPage />} />
            <Route path="discover/marketplace" element={<DiscoverMarketplacePage />} />
            <Route path="dashboard/legal/terms" element={<LegalTermsPage />} />
            <Route path="" element={<Navigate to="/ceramics/dashboard/home" replace />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/ceramics" replace />} />
      </Route>

      {/* Architecture world */}
      <Route path="/architecture/*" element={<ArchitectureWorldLayout />}>
        <Route path="" element={<ArchitectureComingSoon />} />
      </Route>

      {/* Adventure world */}
      <Route path="/adventure/*" element={<AdventureWorldLayout />}>
        <Route path="" element={<AdventureComingSoon />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to={user ? "/" : "/start"} replace />} />
    </Routes>
  );
}

/* =============================
   App (top-level providers)
   ============================= */
export default function App() {
  return (
    <ThemeProvider>
      <VaultProvider>
        <RootRoutes />
        <AccessDebugPanel />
      </VaultProvider>
    </ThemeProvider>
  );
}
